import React, { useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import {
  mdiBarcodeScan,
  mdiClose,
  mdiAlertCircleOutline,
  mdiFileDelimitedOutline,
  mdiUploadOutline,
  mdiDownloadOutline,
} from "@mdi/js";
import Papa from "papaparse";
import { fetchGet } from "../../content_panel/services/serviceClient";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { ScannedBarCodeScanForDeviceFromPTAPI } from "../../../../erp/inventory/stock_transfer/sender_stock_transfer/service/stock_transfer_repo";
import { QrScanner } from "@yudiel/react-qr-scanner";
import EditableDeviceIdsViewer from "../common-components/EditableDeviceIdsViewer";
import { ItemWarehouseModel } from "../model/warehouse_outward_model";

type Props = {
  open: boolean;
  from_warehouse_id: string;
  selectedItem: ItemWarehouseModel;
  onClose: () => void;
  onSave: (devices: string[]) => void;
};

const RejectedDevicesAllocationModal: React.FC<Props> = ({
  open,
  from_warehouse_id,
  selectedItem,
  onClose,
  onSave,
}) => {
  const { showToast } = useToast();

  const [activeTab, setActiveTab] = useState<"scan" | "bulk" | "upload">(
    "scan",
  );
  const [devices, setDevices] = useState<string[]>(
    selectedItem.deviceIds ?? [],
  );
  const [loading, setLoading] = useState(false);
  const [availableCount, setAvailableCount] = useState(0);
  const scanLockRef = useRef(false);
  const devicesRef = useRef<string[]>(selectedItem.deviceIds ?? []);

  const [bulkInput, setBulkInput] = useState("");
  const [barcodeInputValue, setBarcodeInputValue] = useState("");
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  const availableIdsRef = useRef<string[]>([]);

  useEffect(() => {
    if (!open) return;
    fetchAvailableDevices();
  }, [open, from_warehouse_id, selectedItem.inventoryItemCode]);

  useEffect(() => {
    if (!open) return;
    setDevices(selectedItem.deviceIds ?? []);
  }, [open, selectedItem.deviceIds]);

  useEffect(() => {
    devicesRef.current = devices;
  }, [devices]);

  useEffect(() => {
    if (!open || activeTab !== "scan") return;
    const timer = setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 120);
    return () => clearTimeout(timer);
  }, [open, activeTab]);

  useEffect(() => {
    if (!open || activeTab !== "scan") return;

    const timer = setTimeout(() => {
      const clean = barcodeInputValue.trim().replace(/\s+/g, "");
      if (
        clean &&
        !scanLockRef.current &&
        (clean.length === 6 || clean.length === 8)
      ) {
        handleQRDecode(clean);
        setBarcodeInputValue("");
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [barcodeInputValue, open, activeTab]);

  if (!open) return null;

  const fetchAvailableDevices = async () => {
    if (!from_warehouse_id || !selectedItem.inventoryItemCode) {
      showToast({
        type: ToastType.warning,
        text: "Warehouse and Item Code are required",
      });
      return;
    }

    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items/unique-ids?warehouse_id=${from_warehouse_id}&item_code=${selectedItem.inventoryItemCode}`;
      const res = await fetchGet(url);

      if (!res.success) {
        showToast({ type: ToastType.error, text: res.error });
        return;
      }

      const ids =
        res.result?.map((r: any) => r.unique_id?.trim()).filter(Boolean) ?? [];

      availableIdsRef.current = Array.from(new Set(ids));
      setAvailableCount(availableIdsRef.current.length);
    } catch {
      showToast({
        type: ToastType.error,
        text: "Failed to fetch available devices",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQRDecode = async (scannedValue: string) => {
    if (scanLockRef.current) return;

    const id = scannedValue.trim().replace(/\s+/g, "");
    if (!id) return;

    scanLockRef.current = true;

    if (id.length !== 6 && id.length !== 8) {
      showToast({
        type: ToastType.error,
        text: "Please enter a valid scanned ID (6 or 8 characters)",
      });
      scanLockRef.current = false;
      return;
    }

    if (devicesRef.current.includes(id)) {
      showToast({
        type: ToastType.warning,
        text: "Device already scanned",
      });
      scanLockRef.current = false;
      return;
    }

    try {
      setLoading(true);

      const res = await ScannedBarCodeScanForDeviceFromPTAPI(
        selectedItem.inventoryItemCode,
        from_warehouse_id,
        id,
      );

      if (!res.success) {
        showToast({ type: ToastType.error, text: res.error });
        return;
      }

      const validDevices = res.devices
        .map((d: any) => d.unique_id)
        .filter(
          (uid: string) =>
            availableIdsRef.current.includes(uid) &&
            !devicesRef.current.includes(uid),
        );

      if (!validDevices.length) {
        showToast({
          type: ToastType.warning,
          text: "Device already added or not available",
        });
        return;
      }

      const remaining = selectedItem.requestedQty - devicesRef.current.length;
      const toAdd = validDevices.slice(0, remaining);

      setDevices((prev) => {
        const updated = [...prev, ...toAdd];
        devicesRef.current = updated;
        return updated;
      });
    } finally {
      setLoading(false);

      // allow next scan after delay
      setTimeout(() => {
        scanLockRef.current = false;
        if (open && activeTab === "scan") {
          barcodeInputRef.current?.focus();
        }
      }, 800);
    }
  };

  const handleSave = () => {
    const bulkIds =
      activeTab === "bulk"
        ? bulkInput
            .split(/\r?\n/)
            .map((id) => id.trim())
            .filter(Boolean)
        : [];

    // Validate only the newly pasted IDs; the already-allocated (locked) IDs
    // may not be present in the current "available" list.
    const invalid = bulkIds.filter(
      (id) => !availableIdsRef.current.includes(id),
    );

    if (invalid.length) {
      showToast({
        type: ToastType.error,
        text: `Invalid devices detected: ${invalid.join(", ")}`,
      });
      return;
    }

    const nextDevices = Array.from(
      new Set([...(devicesRef.current ?? []), ...bulkIds]),
    );

    if (nextDevices.length > selectedItem.requestedQty) {
      showToast({
        type: ToastType.error,
        text: `Too many device IDs. Required: ${selectedItem.requestedQty}, selected: ${nextDevices.length}`,
      });
      return;
    }

    if (nextDevices.length < selectedItem.requestedQty) {
      showToast({
        type: ToastType.error,
        text: `Allocate exactly ${selectedItem.requestedQty} device IDs. Currently selected: ${nextDevices.length}`,
      });
      return;
    }

    setDevices(nextDevices);
    setBulkInput("");

    showToast({
      type: ToastType.success,
      text: "Device IDs saved successfully",
    });

    onSave(nextDevices);
    onClose();
  };

  const handleCSVUpload = (file: File) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const rows = result.data as any[];
        if (!rows.length) {
          showToast({
            type: ToastType.error,
            text: "CSV is empty or invalid",
          });
          return;
        }

        const required = ["Item Code", "Unique ID", "Item Name"];
        const missing = required.filter((h) => !(h in rows[0]));
        if (missing.length) {
          showToast({
            type: ToastType.error,
            text: `Missing columns: ${missing.join(", ")}`,
          });
          return;
        }

        const ids = rows.map((r) => r["Unique ID"]).filter(Boolean);

        const invalid = ids.filter(
          (id) => !availableIdsRef.current.includes(id),
        );
        if (invalid.length) {
          showToast({
            type: ToastType.error,
            text: `Invalid Device IDs: ${invalid.join(", ")}`,
          });
          return;
        }

        setDevices((prev) => {
          const merged = Array.from(new Set([...prev, ...ids]));
          if (merged.length > selectedItem.requestedQty) {
            showToast({
              type: ToastType.warning,
              text: `Uploaded list exceeds required quantity (${selectedItem.requestedQty}). Remove extra IDs before saving.`,
            });
          }
          return merged;
        });
        showToast({
          type: ToastType.success,
          text: `${ids.length} devices added`,
        });
      },
    });
  };

  const downloadAvailableDevices = () => {
    const ids = availableIdsRef.current;
    if (!ids.length) {
      showToast({ type: ToastType.info, text: "No devices available" });
      return;
    }

    const rows = [["Device ID"], ...ids.map((id) => [id])];
    const csv = "\uFEFF" + rows.map((r) => r.join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "device_ids.csv";
    link.click();

    URL.revokeObjectURL(url);
  };

  const removeOneDevice = (id: string) => {
    setDevices((prev) => prev.filter((d) => d !== id));
  };

  const clearAllDevices = () => {
    setDevices([]);
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40 bg-black bg-opacity-40"
        onClick={(e) => e.stopPropagation()} // REQUIRED
      />

      {/* Modal */}
      <div
        id="warehouse-outward-rejected-devices-allocation-modal"
        className="fixed z-50 rounded-xl bg-white p-6 shadow-2xl border border-gray-200"
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "40rem",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Icon path={mdiBarcodeScan} size={0.9} />
            Add Device IDs – {selectedItem.inventoryItemCode}
          </h2>
          <button
            id="warehouse-outward-rejected-devices-close-icon-btn"
            onClick={onClose}
          >
            <Icon path={mdiClose} size={0.9} />
          </button>
        </div>

        {/* Item Info */}
        <div className="rounded-lg bg-gray-50 border p-4 flex justify-between items-center">
          <div className="flex items-center gap-3 text-sm font-medium">
            Available: {availableCount}
            <button
              id="warehouse-outward-rejected-devices-download-available-btn"
              onClick={downloadAvailableDevices}
              disabled={availableCount === 0}
            >
              <Icon path={mdiDownloadOutline} size={0.7} />
            </button>
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold px-2 py-1 rounded-full bg-gray-200">
            <Icon path={mdiAlertCircleOutline} size={0.6} />
            {devices.length} / {selectedItem.requestedQty}
          </div>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-3 bg-gray-100 rounded-md mt-4">
          {[
            { key: "scan", label: "Scan", icon: mdiBarcodeScan },
            { key: "bulk", label: "Bulk Paste", icon: mdiFileDelimitedOutline },
            { key: "upload", label: "Upload CSV", icon: mdiUploadOutline },
          ].map((t) => (
            <button
              key={t.key}
              id={`warehouse-outward-rejected-devices-tab-${t.key}`}
              onClick={() => setActiveTab(t.key as any)}
              className={`flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md ${
                activeTab === t.key ? "bg-white shadow" : "text-gray-500"
              }`}
            >
              <Icon path={t.icon} size={0.7} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Scan */}
        {activeTab === "scan" && (
          <div className="mt-4 space-y-2 mb-4">
            <div className="flex items-center gap-2 mb-1">
              <Icon
                path={mdiBarcodeScan}
                size={0.6}
                className="text-blue-600"
              />
              <label className="text-xs font-bold text-gray-700 uppercase tracking-tight">
                Hardware Barcode Scanner
              </label>
            </div>
            <input
              id="warehouse-outward-rejected-devices-barcode-input"
              ref={barcodeInputRef}
              type="text"
              className="w-full h-10 rounded-md border px-3 text-sm"
              placeholder="Scan barcode or type ID and press Enter"
              value={barcodeInputValue}
              onChange={(e) => setBarcodeInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && barcodeInputValue.trim()) {
                  e.preventDefault();
                  const clean = barcodeInputValue.trim().replace(/\s+/g, "");
                  handleQRDecode(clean);
                  setBarcodeInputValue("");
                }
              }}
            />
            <div className="relative flex items-center py-2">
              <div className="flex-grow border-t border-gray-200"></div>
              <span className="flex-shrink mx-4 text-gray-400 text-[10px] font-bold">
                Or Scan Via Camera
              </span>
              <div className="flex-grow border-t border-gray-200"></div>
            </div>
            <div className="mx-auto w-3/4 max-w-sm h-[180px] rounded-lg overflow-hidden border">
              <QrScanner
                constraints={{ facingMode: "environment" }}
                onDecode={(result) => {
                  if (result) handleQRDecode(result);
                }}
                onError={(err) => {
                  console.error(err);
                  showToast({
                    type: ToastType.error,
                    text: "Camera error",
                  });
                }}
                containerStyle={{
                  width: "100%",
                  height: "100%",
                }}
                videoStyle={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </div>
          </div>
        )}

        {/* Bulk */}
        {activeTab === "bulk" && (
          <div className="space-y-2">
            <textarea
              id="warehouse-outward-rejected-devices-bulk-textarea"
              rows={5}
              className="w-full mt-4 rounded-md border p-3 text-sm"
              placeholder="One device ID per line"
              value={bulkInput}
              onChange={(e) => setBulkInput(e.target.value)}
            />
          </div>
        )}

        {/* Upload */}
        {activeTab === "upload" && (
          <input
            id="warehouse-outward-rejected-devices-csv-upload-input"
            type="file"
            accept=".csv"
            className="mt-4"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleCSVUpload(file);
            }}
          />
        )}

        {/* Footer */}
        {activeTab !== "scan" && (
          <div className="flex gap-3 mt-2 mb-2">
            <button
              id="warehouse-outward-rejected-devices-cancel-btn"
              className="flex-1 h-10 rounded-md border text-sm font-medium"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              id="warehouse-outward-rejected-devices-save-btn"
              onClick={handleSave}
              disabled={loading}
              className="flex-1 h-10 rounded-md bg-blue-600 text-white text-sm font-medium disabled:opacity-50"
            >
              Save Device IDs
            </button>
          </div>
        )}

        {devices && (
          <EditableDeviceIdsViewer
            deviceIds={devices}
            onRemoveOne={removeOneDevice}
            onClearAll={clearAllDevices}
          />
        )}
      </div>
    </>
  );
};

export default RejectedDevicesAllocationModal;
